import React, { useEffect, useMemo, useState } from "react";
import "./PurchaseHistory.css";
import { useLogin } from "../LoginProvider";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  startAfter,
} from "firebase/firestore";

const PAGE_SIZE = 10;

// ---------- Helpers ----------
const safeTextPtch = (v, fallback = "—") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s.length ? s : fallback;
};

const moneyPtch = (n) => Number(n || 0).toLocaleString("fr-FR");

const datePtch = (ts) => {
  if (!ts) return "—";
  if (ts?.toDate) {
    return ts.toDate().toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return safeTextPtch(ts);
};

const orderCodePtch = (o) => o?.code || o?.invoiceCode || o?.id || "—";

const optionLabelPtch = (o) => {
  const opt = String(o?.Option || "").toLowerCase();
  if (opt === "livraison") return "Livraison";
  if (opt === "expedition") return "Expédition";
  if (opt === "boutique" || opt === "recuperation") return "Récupération boutique";
  return safeTextPtch(o?.Option, "—");
};

// ✅ Statut (badge) basé sur Paid + Etat
const getStatusPtch = (o) => {
  const etat = String(o?.Etat || "").toLowerCase();
  const paid = o?.Paid === true;

  if (etat === "cancel" || etat === "cancelled" || etat === "canceled") {
    return { label: "Annulée", tone: "danger" };
  }

  if (!paid) return { label: "Non payée", tone: "warning" };

  // payée
  if (etat === "delivery") return { label: "Payée", tone: "success" };
  if (etat === "cours") return { label: "En cours", tone: "success" };
  if (etat === "pending") return { label: "En attente", tone: "neutral" };

  return { label: "Payée", tone: "success" };
};

// ✅ Progress selon tes règles
const progressPtch = (o) => {
  const etat = String(o?.Etat || "").toLowerCase();
  const paid = o?.Paid === true;

  if (!paid) return 10;
  if (etat === "delivery") return 100;
  if (etat === "pending") return 50;
  if (etat === "cours") return 100;

  // fallback si payé mais état inconnu
  return 60;
};

// ---------- Modal ----------
function OrderModalPtch({ open, onClose, order }) {
  if (!open || !order) return null;

  const items = Array.isArray(order.OrdersItems) ? order.OrdersItems : [];
  const subtotal = Number(order.TotalPrice ?? 0);
  const delivery = Number(order.DeliveryFee ?? 0);
  const total = Number(order.AfterTotal ?? (subtotal + delivery));

  const taxe = Number(order.TaxeFee ?? 0);

  const status = getStatusPtch(order);

  return (
    <div className="ptch-modalOverlay-ptch" onMouseDown={onClose}>
      <div
        className="ptch-modal-ptch"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ptch-modalHeader-ptch">
          <div className="ptch-modalTitleWrap-ptch">
            <div className="ptch-modalTitle-ptch">Détails de la commande</div>
            <div className="ptch-modalSub-ptch">
              <span className="ptch-codePill-ptch">Commande# {safeTextPtch(orderCodePtch(order))}</span>
              <span className={`ptch-badge-ptch tone-${status.tone}-ptch`}>
                <span className={`ptch-badgeDot-ptch tone-${status.tone}-ptch`} />
                {status.label}
              </span>
            </div>
          </div>

          <button className="ptch-closeBtn-ptch" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </div>

        <div className="ptch-modalBody-ptch">
          <div className="ptch-summaryGrid-ptch">
            <div className="ptch-summaryCard-ptch">
              <div className="ptch-summaryLabel-ptch">Date</div>
              <div className="ptch-summaryValue-ptch">{datePtch(order.createdAt)}</div>
            </div>

            <div className="ptch-summaryCard-ptch">
              <div className="ptch-summaryLabel-ptch">Type</div>
              <div className="ptch-summaryValue-ptch">{optionLabelPtch(order)}</div>
            </div>

            <div className="ptch-summaryCard-ptch">
              <div className="ptch-summaryLabel-ptch">Téléphone</div>
              <div className="ptch-summaryValue-ptch">
                {safeTextPtch(order.Telephone || order.phone_user || order.customer_phone_number)}
              </div>
            </div>

            <div className="ptch-summaryCard-ptch">
              <div className="ptch-summaryLabel-ptch">Adresse</div>
              <div className="ptch-summaryValue-ptch">
                {safeTextPtch(order.Adresse || order.location || order.customer_address)}
              </div>
            </div>
          </div>

          <div className="ptch-section-ptch">
            <div className="ptch-sectionTitle-ptch">{items.length} Article{items.length > 1 ? "s" : ""}</div>

            <div className="ptch-items-ptch">
              {items.length === 0 ? (
                <div className="ptch-empty-ptch">Aucun article.</div>
              ) : (
                items.map((it, idx) => {
                  const qty = Number(it.qty || 0);
                  const price = Number(it.price || 0);
                  const line = qty * price;

                  return (
                    <div className="ptch-itemRow-ptch" key={it.id || idx}>
                      <div className="ptch-itemLeft-ptch">
                        <div className="ptch-itemImgWrap-ptch">
                          <img
                            className="ptch-itemImg-ptch"
                            src={it.image}
                            alt={safeTextPtch(it.title, "Produit")}
                          />
                        </div>

                        <div className="ptch-itemInfo-ptch">
                          <div className="ptch-itemTitle-ptch">{safeTextPtch(it.title)}</div>
                          <div className="ptch-itemMeta-ptch">
                            Prix: <strong>{moneyPtch(price)} FCFA</strong> • Qté:{" "}
                            <strong>{qty}</strong>
                          </div>
                          <div
  className="ptch-receivedWrap-ptch"
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start", // ← aligne à gauche
    gap: 4,
    marginTop: 8
  }}
>
  {it.Received === true ? (
    <span
      className="ptch-codePill-ptch-badge"
      style={{
        background: "#dcfce7",
        color: "#15803d",
        border: "1px solid #86efac",
        padding: "4px 10px",
        borderRadius: "999px",
        fontWeight: 800,
        fontSize: 12
      }}
    >
      Received
    </span>
  ) : (
    <>
      <span
        className="ptch-codePill-ptch-badge"
        style={{
          background: "#fee2e2",
          color: "#b91c1c",
          border: "1px solid #fca5a5",
          padding: "4px 10px",
          borderRadius: "999px",
          fontWeight: 800,
          fontSize: 12
        }}
      >
        No received
      </span>

      {it.ReceivedNote && (
        <div
          style={{
            fontSize: 12,
            color: "#444",
            fontWeight: 600
          }}
        >
          {it.ReceivedNote}
        </div>
      )}
    </>
  )}
</div>
                        </div>
                      </div>

                      <div className="ptch-itemRight-ptch">
                        <div className="ptch-itemLine-ptch">{moneyPtch(line)} FCFA</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="ptch-section-ptch">
            <div className="ptch-sectionTitle-ptch">Récapitulatif</div>

            <div className="ptch-totals-ptch">
              <div className="ptch-totalRow-ptch">
                <span>Sous-total</span>
                <strong>{moneyPtch(subtotal)} FCFA</strong>
              </div>
              <div className="ptch-totalRow-ptch">
                <span>Livraison</span>
                <strong>{moneyPtch(delivery)} FCFA</strong>
              </div>
              <div className="ptch-totalRow-ptch ptch-totalRowBig-ptch">
                <span>Taxe</span>
                <strong>{moneyPtch(taxe)} FCFA</strong>
              </div>
              <div className="ptch-totalRow-ptch ptch-totalRowBig-ptch">
                <span>Total</span>
                <strong>{moneyPtch(total)} FCFA</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="ptch-modalFooter-ptch">
          <button className="ptch-secondaryBtn-ptch" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Component ----------
export default function PurchaseHistory() {
  const { user } = useLogin();

  const [searchPtch, setSearchPtch] = useState("");
  const [loadingPtch, setLoadingPtch] = useState(false);
  const [ordersPtch, setOrdersPtch] = useState([]);

  const [nextCursorPtch, setNextCursorPtch] = useState(null);
  const [hasMorePtch, setHasMorePtch] = useState(false);
  const [firstLoadDonePtch, setFirstLoadDonePtch] = useState(false);

  const [modalOpenPtch, setModalOpenPtch] = useState(false);
  const [selectedOrderPtch, setSelectedOrderPtch] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchFirst = async () => {
      setLoadingPtch(true);
      try {
        const q = query(
          collection(db, "ecom_orders"),
          where("uid", "==", user.id),
          orderBy("createdAt", "desc"),
          limit(PAGE_SIZE)
        );

        const snap = await getDocs(q);
        if (cancelled) return;

        let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // ✅ filtre: seulement Etat != "cancel"
        rows = rows.filter((o) => {
          const etat = String(o?.Etat || "").toLowerCase();
          return etat !== "cancel" && etat !== "cancelled" && etat !== "canceled";
        });

        setOrdersPtch(rows);
        setFirstLoadDonePtch(true);

        const last = snap.docs[snap.docs.length - 1] || null;
        setNextCursorPtch(last);
        setHasMorePtch(Boolean(last) && snap.docs.length === PAGE_SIZE);
      } catch (e) {
        console.error("fetch orders error:", e);
      } finally {
        if (!cancelled) setLoadingPtch(false);
      }
    };

    fetchFirst();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const loadMorePtch = async () => {
    if (!user || !hasMorePtch || !nextCursorPtch) return;
    setLoadingPtch(true);

    try {
      const q = query(
        collection(db, "ecom_orders"),
        where("uid", "==", user.id),
        orderBy("createdAt", "desc"),
        startAfter(nextCursorPtch),
        limit(PAGE_SIZE)
      );

      const snap = await getDocs(q);

      let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows = rows.filter((o) => {
        const etat = String(o?.Etat || "").toLowerCase();
        return etat !== "cancel" && etat !== "cancelled" && etat !== "canceled";
      });

      setOrdersPtch((prev) => [...prev, ...rows]);

      const last = snap.docs[snap.docs.length - 1] || null;
      setNextCursorPtch(last);
      setHasMorePtch(Boolean(last) && snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error("load more error:", e);
    } finally {
      setLoadingPtch(false);
    }
  };

  const filteredPtch = useMemo(() => {
    const term = searchPtch.trim().toLowerCase();
    if (!term) return ordersPtch;

    return ordersPtch.filter((o) => {
      const code = String(orderCodePtch(o)).toLowerCase();
      const name = String(o?.name || "").toLowerCase();
      const phone = String(o?.Telephone || o?.phone_user || "").toLowerCase();
      const etat = String(o?.Etat || "").toLowerCase();
      return code.includes(term) || name.includes(term) || phone.includes(term) || etat.includes(term);
    });
  }, [ordersPtch, searchPtch]);

  const openDetailsPtch = (o) => {
    setSelectedOrderPtch(o);
    setModalOpenPtch(true);
  };

  const closeDetailsPtch = () => {
    setModalOpenPtch(false);
    setSelectedOrderPtch(null);
  };

  return (
    <div className="ptch-page-ptch">
      <div className="ptch-card-ptch">
        <div className="ptch-top-ptch">
          <div className="ptch-titleWrap-ptch">
            <div className="ptch-title-ptch">Historique d’achats</div>
            <div className="ptch-subtitle-ptch">
              {user ? "Vos commandes" : "Connectez-vous pour voir vos achats"}
            </div>
          </div>

          <div className="ptch-searchWrap-ptch">
            <span className="ptch-searchIcon-ptch">⌕</span>
            <input
              className="ptch-search-ptch"
              value={searchPtch}
              onChange={(e) => setSearchPtch(e.target.value)}
              placeholder="Rechercher par code, nom, téléphone, statut…"
            />
          </div>
        </div>

        <div className="ptch-tableWrap-ptch">
          <table className="ptch-table-ptch">
            <thead className="ptch-thead-ptch">
              <tr>
                <th className="ptch-th-ptch">Commande</th>
                <th className="ptch-th-ptch">Date</th>
                <th className="ptch-th-ptch">Statut</th>
                <th className="ptch-th-ptch">Traitement</th>
                <th className="ptch-th-ptch">Type</th>
                <th className="ptch-th-ptch">Montant</th>
                <th className="ptch-th-ptch">Progress</th>
                <th className="ptch-th-ptch ptch-thActions-ptch"></th>
              </tr>
            </thead>

            <tbody className="ptch-tbody-ptch">
              {!user && (
                <tr>
                  <td colSpan={7} className="ptch-empty-ptch">
                    Veuillez vous connecter pour afficher votre historique.
                  </td>
                </tr>
              )}

              {user && firstLoadDonePtch && filteredPtch.length === 0 && (
                <tr>
                  <td colSpan={7} className="ptch-empty-ptch">
                    Aucun achat trouvé.
                  </td>
                </tr>
              )}

              {user &&
                filteredPtch.map((o) => {
                  const status = getStatusPtch(o);
                  const p = progressPtch(o);
                  const code = orderCodePtch(o);
                  const total = o?.AfterTotal ?? o?.TotalPrice ?? 0;

                  return (
                    <tr key={o.id} className="ptch-row-ptch">
                      <td className="ptch-td-ptch">
                        <div className="ptch-cellMain-ptch">{safeTextPtch(code)}</div>
                      </td>

                      <td className="ptch-td-ptch">
                        <div className="ptch-cellMain-ptch">{datePtch(o.createdAt)}</div>
                      </td>

                      <td className="ptch-td-ptch">
                        <span className={`ptch-badge-ptch tone-${status.tone}-ptch`}>
                          <span className={`ptch-badgeDot-ptch tone-${status.tone}-ptch`} />
                          {status.label}
                        </span>
                      </td>

                      <td className="ptch-td-ptch">
                        <div
                            className="ptch-statusBadge-ptch"
                            style={{
                            padding: "4px 10px",
                            borderRadius: "999px",
                            fontWeight: 700,
                            fontSize: 12,
                            display: "inline-block",
                            background:
                                o.Etat === "delivery"
                                ? "#dcfce7"
                                : o.Etat === "pending"
                                ? "#fef3c7"
                                : o.Etat === "cours"
                                ? "#dbeafe"
                                : "#fee2e2",
                            color:
                                o.Etat === "delivery"
                                ? "#15803d"
                                : o.Etat === "pending"
                                ? "#92400e"
                                : o.Etat === "cours"
                                ? "#1d4ed8"
                                : "#b91c1c",
                            }}
                        >
                            {o.Etat === "delivery"
                            ? "Delivered"
                            : o.Etat === "pending"
                            ? "Pending"
                            : o.Etat === "cours"
                            ? "In progress"
                            : "En Preparation"}
                        </div>
                        </td>

                      <td className="ptch-td-ptch">
                        <span className="ptch-pill-ptch">{optionLabelPtch(o)}</span>
                      </td>

                      <td className="ptch-td-ptch">
                        <div className="ptch-cellMain-ptch">{moneyPtch(total)} FCFA</div>
                      </td>

                      <td className="ptch-td-ptch">
                        <div className="ptch-progress-ptch">
                          <div className="ptch-progressBar-ptch">
                            <div className="ptch-progressFill-ptch" style={{ width: `${p}%` }} />
                          </div>
                          <div className="ptch-progressVal-ptch">{p}%</div>
                        </div>
                      </td>

                      <td className="ptch-td-ptch ptch-actions-ptch">
                        <button
                          type="button"
                          className="ptch-detailsBtn-ptch"
                          onClick={() => openDetailsPtch(o)}
                        >
                          Détails
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <div className="ptch-footer-ptch">
          {loadingPtch && <div className="ptch-loading-ptch">Chargement…</div>}

          {!loadingPtch && user && hasMorePtch && (
            <button className="ptch-loadMore-ptch" onClick={loadMorePtch} type="button">
              Charger plus
            </button>
          )}

          {!loadingPtch && user && !hasMorePtch && firstLoadDonePtch && ordersPtch.length > 0 && (
            <div className="ptch-end-ptch">Fin de la liste.</div>
          )}
        </div>
      </div>

      <OrderModalPtch open={modalOpenPtch} onClose={closeDetailsPtch} order={selectedOrderPtch} />
    </div>
  );
}